'use client';

import { createContext, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

/** Shared between ShowcaseDeviceFrame and every Hotspot inside it, so a popover can portal into
 * the frame's own overflow-visible layer (escaping the clipped screen) and the frame can reserve
 * extra bottom space while a popover is open instead of letting it float over later content.
 * The layer is exposed as a ref-reading getter (not reactive state) so it is available the
 * instant a hotspot is clicked, without waiting on an extra render cycle after mount. */
const ShowcaseFrameContext = createContext<{
  getPopoverLayer: () => HTMLDivElement | null;
  openHotspotId: string | null;
  setOpenHotspotId: (id: string | null) => void;
} | null>(null);

export function useShowcaseFrame() {
  return useContext(ShowcaseFrameContext);
}

/** Consistent app-window chrome wrapping every product preview -- a plain rounded frame with a
 * traffic-light dot row and a label, never a literal OS browser/taskbar capture. */
export default function ShowcaseDeviceFrame({
  children,
  label,
}: {
  children: ReactNode;
  label: string;
}) {
  const popoverLayerRef = useRef<HTMLDivElement | null>(null);
  const [openHotspotId, setOpenHotspotId] = useState<string | null>(null);
  const frameContextValue = useMemo(
    () => ({
      getPopoverLayer: () => popoverLayerRef.current,
      openHotspotId,
      setOpenHotspotId,
    }),
    [openHotspotId]
  );

  return (
    <div
      className="relative w-full min-w-0 max-w-full overflow-visible transition-[margin] duration-150 ease-out motion-reduce:transition-none"
      style={{ marginBottom: openHotspotId ? 160 : 0 }}
    >
      <div className="overflow-hidden rounded-[20px] border border-[#1d9e75]/20 bg-[#0f211d] shadow-[0_24px_60px_rgba(10,20,18,0.16)]">
        <div className="flex items-center gap-2 border-b border-white/10 bg-[#0a1614] px-4 py-2.5">
          <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full bg-[#e88a73]/70" />
          <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full bg-[#d9a441]/70" />
          <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full bg-[#1d9e75]/70" />
          <span className="ml-2 truncate font-mono text-[11px] font-medium text-[#9db4aa]">
            {label}
          </span>
        </div>
        <div className="relative min-w-0 max-w-full overflow-hidden bg-[var(--public-surface)] p-4 sm:p-6">
          <ShowcaseFrameContext.Provider value={frameContextValue}>
            {children}
          </ShowcaseFrameContext.Provider>
        </div>
      </div>

      {/* Overflow-visible layer, a sibling of the clipped screen above, sitting inside this
          frame's own overflow-visible wrapper. Hotspot popovers portal in here so they can
          render outside the rounded/clipped screen without being cut off. No aria-hidden here:
          a real, accessible popover gets portaled into this layer while open, and aria-hidden
          on an ancestor would incorrectly hide it from the accessibility tree too. The layer
          itself renders no visible content of its own when empty, so it needs no hidden state. */}
      <div
        ref={popoverLayerRef}
        className="pointer-events-none absolute inset-0 z-[45] overflow-visible"
      />
    </div>
  );
}
